const HEIF_MIME = new Set([
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
]);

const HEIF_EXT = /\.(heic|heif|hif)$/i;

const JPEG_QUALITY = 0.92;

const ALREADY_WEB_IMAGE = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
]);

function fileLooksHeifByNameOrType(file) {
  const type = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '');
  return HEIF_MIME.has(type) || HEIF_EXT.test(name);
}

export function isLikelyPhotoFile(file) {
  if (!file) return false;
  const type = String(file.type || '').toLowerCase();
  if (type.startsWith('image/')) return true;
  return HEIF_EXT.test(file.name || '');
}

/** ISO-BMFF `ftyp` brand used by HEIF/HEIC (and some iPhone stills tagged `mif1`). */
function ftypBrandLooksHeif(bytes) {
  if (!bytes || bytes.length < 12) return false;
  const ftyp = String.fromCharCode(bytes[4], bytes[5], bytes[6], bytes[7]);
  if (ftyp !== 'ftyp') return false;
  const brand = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]);
  return /^(heic|heix|heif|heim|heis|mif1|msf1)$/i.test(brand);
}

async function sniffHeifContainer(file) {
  try {
    const buf = await file.slice(0, 16).arrayBuffer();
    return ftypBrandLooksHeif(new Uint8Array(buf));
  } catch {
    return false;
  }
}

function jpegFileFromBlob(blob, originalName) {
  const base = String(originalName || 'photo').replace(HEIF_EXT, '') || 'photo';
  return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
}

/** Safari / some OS decoders can rasterize HEIF; Chrome/Firefox desktop usually cannot. */
async function tryNativeDecodeToJpeg(file) {
  if (typeof createImageBitmap !== 'function') return null;
  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return null;
  }
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0);
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('toBlob failed'))),
        'image/jpeg',
        JPEG_QUALITY,
      );
    });
    return blob;
  } catch {
    return null;
  } finally {
    bitmap.close?.();
  }
}

async function convertHeifWithWasm(file) {
  const heic2any = (await import('heic2any')).default;
  const result = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: JPEG_QUALITY,
  });
  return Array.isArray(result) ? result[0] : result;
}

/**
 * Desktop Chrome/Firefox cannot paint HEIF/HEIC in `<img>`. iOS often converts on pick
 * or can decode natively — convert to JPEG before upload/preview so every client matches.
 */
export async function ensureBrowserDisplayablePhotoFile(file) {
  if (!file) return file;
  const type = String(file.type || '').toLowerCase();
  if (ALREADY_WEB_IMAGE.has(type)) return file;
  const looksHeif = fileLooksHeifByNameOrType(file) || await sniffHeifContainer(file);
  if (!looksHeif) return file;

  const nativeJpeg = await tryNativeDecodeToJpeg(file);
  if (nativeJpeg) return jpegFileFromBlob(nativeJpeg, file.name);

  const wasmJpeg = await convertHeifWithWasm(file);
  if (!wasmJpeg) throw new Error('HEIF conversion produced an empty file');
  return jpegFileFromBlob(wasmJpeg, file.name);
}
